/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : LinkHomePage.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Grid } from "@mui/material";
import React, { useEffect, useRef } from "react";
import CustomTabsV2 from "../../common/tabs/CustomTabsV2";
import EELinkPage from "./EELinkPage";
import SWMLinkPage from "./SWMLinkPage";
import ProjectVariantSearchbar from "../../common/projectSearchbar/ProjectvariantSearchbar";
import ProjectVariantDialog from "../ProjectVarientAccordian/ProjectVariantDialog";
import CommonBreadcrumbs from "../../common/Breadcrumbs/CommonBreadcrumbs";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

function LinkHomePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const tabParamRaw = queryParams.get("tab") ?? queryParams.get("tabFromurl");
  const tabFromurl = Number(tabParamRaw);

  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );

  const prevProjectIdRef = useRef(selectedProject?.project_id);
  const prevVariantIdRef = useRef(selectedVariant?.variant_id);

  useEffect(() => {
    const currentProjectId = selectedProject?.project_id;
    const currentVariantId = selectedVariant?.variant_id;

    const isProjectChanged =
      Boolean(prevProjectIdRef.current) &&
      Boolean(currentProjectId) &&
      prevProjectIdRef.current !== currentProjectId;

    const isVariantChanged =
      Boolean(prevVariantIdRef.current) &&
      Boolean(currentVariantId) &&
      prevVariantIdRef.current !== currentVariantId;

    if (isProjectChanged || isVariantChanged) {
      if (location.search) {
        const params = new URLSearchParams(location.search);
        params.delete("ee_id");
        params.delete("swm_id");
        params.delete("zone");
        params.delete("selectedZone");
        params.delete("zonePosition");
        params.delete("tab");
        params.delete("tabFromurl");

        const newSearch = params.toString();
        navigate(
          newSearch ? `${location.pathname}?${newSearch}` : location.pathname,
          { replace: true },
        );
      }
    }

    if (currentProjectId) {
      prevProjectIdRef.current = currentProjectId;
    }
    if (currentVariantId) {
      prevVariantIdRef.current = currentVariantId;
    }
  }, [
    selectedProject?.project_id,
    selectedVariant?.variant_id,
    location.pathname,
    location.search,
    navigate,
  ]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <Box sx={{ p: 1 }}>
          <CommonBreadcrumbs
            items={[
              { label: "Virtual Lab-Car" },
              { label: "Linked Components" },
            ]}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            ml: 1,
          }}
        >

          <Box>
            <ProjectVariantSearchbar />
          </Box>
          <ProjectVariantDialog />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <CustomTabsV2
          defaultTab={Number.isNaN(tabFromurl) ? 0 : tabFromurl}
          tabs={[
            {
              name: "EE Component",
              count: "",
              content: <EELinkPage />,
            },
            {
              name: "Software Module",
              count: "",
              content: <SWMLinkPage />,
            },
          ]}
          centerTabs={true}
        />
      </Grid>
    </Grid>
  );
}

export default LinkHomePage;
